import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import {
  FilterOption,
  FilterOptionItem,
} from '@orderna/admin-panel/src/types/filter';
import { hasGroup, isFilterOptionGroup } from '@orderna/admin-panel/src/utils/filter';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';

@Directive({
  selector: '[appInputSelectContentIfEmpty]',
  standalone: true,
})
export class InputSelectContentIfEmptyDirective {
  constructor(public templateRef: TemplateRef<unknown>) {}
}

@Component({
  selector: 'app-input-select',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  host: { hostID: Math.random().toString(36).substring(2) + Date.now().toString(36) },
})
export class InputSelectComponent<T, K extends keyof T>
  implements OnInit, OnDestroy, AfterContentInit
{
  @Input() id!: string;
  @Input() label!: string;
  @Input() controlName!: string;
  @Input() options!: FilterOption<T, K>[];
  @Input() defaultOption!: FilterOptionItem<T[K]>;
  @Input() validators: ValidatorFn[] = [];
  @Input() value?: string | T[K];
  @Input() allowMultipleSelection: boolean = false;
  @Input() placeholder?: string;
  @Input() resettable: boolean = false;
  @Input() showDescription: boolean = true;
  @Input() removeCtrlOnDestroy: boolean = true;

  @Output() valueChanges = new EventEmitter<string | T[K]>();

  @ContentChild(InputSelectContentIfEmptyDirective)
  contentIfEmpty!: InputSelectContentIfEmptyDirective;

  hasContentIfEmpty = false;
  control!: FormControl;
  parentContainer = inject(ControlContainer);
  isFilterOptionGroup = isFilterOptionGroup;

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get required() {
    return this.control.hasValidator(Validators.required);
  }

  ngOnInit(): void {
    this.control = this.getOrCreateControl();

    this.control.valueChanges.subscribe((value) => {
      this.valueChanges.emit(value);
    });
  }

  ngAfterContentInit(): void {
    this.hasContentIfEmpty = !!this.contentIfEmpty;
  }

  ngOnDestroy() {
    if (!this.removeCtrlOnDestroy) return;
    this.parentFormGroup?.removeControl(this.controlName);
  }

  private getOrCreateControl(): FormControl {
    const existingControl = this.parentFormGroup.get(
      this.controlName
    ) as FormControl;

    if (existingControl) {
      this.addValidators(existingControl);
      return existingControl;
    }

    return this.createNewControl();
  }

  private createNewControl(): FormControl {
    const newControl = new FormControl(
      this.value ?? this.defaultOption?.value,
      [...this.validators]
    );
    this.parentFormGroup.addControl(this.controlName, newControl);
    return newControl;
  }

  private addValidators(control: FormControl): void {
    if (this.validators.length === 0) return;
    control.addValidators(this.validators);
    control.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].isFirstChange()) {
      this.control.setValue(this.value);
    }
    if (changes['defaultOption'] && !changes['defaultOption'].isFirstChange()) {
      this.control.setValue(this.defaultOption?.value);
    }
  }

  trackById(index: number, option: any) {
    return option.id ? option.id : index;
  }

  getTriggerLabel(): string | null {
    const options = this.options;
    const value = this.control.value;

    // Helper to flatten grouped options
    function flattenOptions(opts: FilterOption<T, K>[]): FilterOptionItem<T[K]>[] {
      return opts.flatMap(option =>
        isFilterOptionGroup(option) ? option.options : [option]
      );
    }

    // If options are grouped, flatten them for easier searching
    const flatOptions = hasGroup(options) ? flattenOptions(options) : options as FilterOptionItem<T[K]>[];

    if (this.allowMultipleSelection) {
      if (Array.isArray(value)) {
        const selectedLabels = flatOptions
          .filter(option => value.includes(option.value))
          .map(option => option.label);
        return selectedLabels.length > 0 ? selectedLabels.join(', ') : null;
      }
    } else {
      const found = flatOptions.find(option => value === option.value);
      return found ? found.label : null;
    }

    return null;
  }
}
